import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  Injector,
  input,
  linkedSignal,
  resource,
  signal,
  TemplateRef,
  viewChild,
} from '@angular/core';
import {
  ButtonComponent,
  IconComponent,
  MessageBarService,
  QuantityInputComponent,
  ResolvableModal,
  SoftSlotDirective,
  TranslatePipe,
  translator,
} from '@softline/ui-core';
import { ProgressStep, ProgressStepsComponent } from '@softline/ui-core/progress';
import { block, handleRequestError } from '@softline/application';
import { ResourceContainerComponent } from '@softapps/core';
import {
  ArtikelMenge,
  BatchInfoComponent,
  Charge,
  Einheit,
  EinheitAuswahlItem,
  formatDimension,
  Lagerplatz,
  LagerplatzInhalt,
  LagerplatzSucheComponent,
  openSelectUnitDialog,
} from '@softapps/lager/core';
import { StockTransferService } from '../../services/stock-transfer.service';
import {
  EinheitenUmbuchung,
  EinheitenUmbuchungVorschlag,
} from '../../types/einheiten-umbuchung';

export interface StockRow {
  lagerplatz: Lagerplatz;
  unit: EinheitAuswahlItem;
  units: EinheitAuswahlItem[];
  charge: Charge | null;
  mhd: string | null;
}

/** One Lagerplatz can hold several charges of an article: the key names the row, not the place. */
export function stockRowKey(row: StockRow): string {
  return `${row.lagerplatz.id}-${row.unit.einheit.id}-${row.charge?.id ?? 0}-${row.mhd ?? ''}`;
}

export interface TargetRow {
  unit: EinheitAuswahlItem;
  proposal: EinheitenUmbuchungVorschlag | null;
  quantity: number;
  loading: boolean;
}

export interface StockTransferTarget {
  einheit: Einheit;
  quantity: number;
}

export interface StockTransferSelection {
  idartikel: number;
  source: StockRow;
  quantity: number;
  targets: StockTransferTarget[];
  targetLocation: Lagerplatz | null | undefined;
}

export function toStockRows(stock: LagerplatzInhalt[]): StockRow[] {
  return stock.flatMap((entry) =>
    entry.inhalt.flatMap((content) => {
      const units = content.einheitenAuswahl ?? [];
      return units.map((unit) => ({
        lagerplatz: entry.lagerplatz,
        unit,
        units,
        charge: content.charge ?? null,
        mhd: content.mhd ?? null,
      }));
    })
  );
}

export function buildStockTransfer(
  selection: StockTransferSelection
): EinheitenUmbuchung {
  const artikel = { id: selection.idartikel };
  const targetLocation = selection.targetLocation;
  return {
    quellLagerplatz: { id: selection.source.lagerplatz.id },
    zielLagerplatz:
      targetLocation && targetLocation.id !== selection.source.lagerplatz.id
        ? { id: targetLocation.id }
        : null,
    quellMengen: [
      {
        artikel,
        einheit: { id: selection.source.unit.einheit.id },
        menge: selection.quantity,
      },
    ],
    zielMengen: selection.targets
      .filter((target) => target.quantity > 0)
      .map((target) => ({
        artikel,
        einheit: { id: target.einheit.id },
        menge: target.quantity,
      })),
  };
}

export function proposedQuantity(row: TargetRow): number {
  return row.proposal?.zielMenge?.menge ?? 0;
}

@Component({
  selector: 'soft-einheiten-umbuchung-modal',
  imports: [
    ButtonComponent,
    IconComponent,
    SoftSlotDirective,
    ProgressStepsComponent,
    QuantityInputComponent,
    ResourceContainerComponent,
    LagerplatzSucheComponent,
    TranslatePipe,
    BatchInfoComponent,
  ],
  templateUrl: './einheiten-umbuchung-modal.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [StockTransferService],
})
export class EinheitenUmbuchungModalComponent extends ResolvableModal<EinheitenUmbuchung> {
  private readonly service = inject(StockTransferService);
  private readonly messageBar = inject(MessageBarService);
  private readonly injector = inject(Injector);
  private readonly openSelectUnitDialog = openSelectUnitDialog();
  private readonly t = translator();

  readonly formatDimension = formatDimension;
  readonly proposedQuantity = proposedQuantity;
  readonly stockRowKey = stockRowKey;

  readonly artikelMenge = input.required<ArtikelMenge>();
  readonly idlagerstamm = input<number>();

  override readonly bottomTemplate = viewChild('bottomTemplate', { read: TemplateRef });

  private readonly stepTitles = ['SOURCE', 'QUANTITY', 'TARGET', 'LOCATION'].map((key) =>
    this.t(`#KOMMISSIONIERUNG.DIALOGS.EINHEITEN_UMBUCHUNG.STEPS.${key}`)
  );
  readonly steps = computed<ProgressStep[]>(() =>
    this.stepTitles.map((title) => ({ header: title() }))
  );
  readonly stepIndex = signal(0);
  readonly isLastStep = computed(() => this.stepIndex() === this.stepTitles.length - 1);

  readonly stock = resource({
    params: () => ({
      id: this.artikelMenge().artikel.id,
      idlagerstamm: this.idlagerstamm(),
    }),
    loader: ({ params }) => this.service.loadStock(params.id, params.idlagerstamm),
  });
  readonly rows = computed(() =>
    this.stock.hasValue() ? toStockRows(this.stock.value()) : []
  );

  readonly source = signal<StockRow | null>(null);
  readonly quantity = linkedSignal(() => Math.min(1, this.source()?.unit.menge ?? 0));
  readonly stockHint = computed(() => {
    const source = this.source();
    if (!source) return null;
    return this.t('#KOMMISSIONIERUNG.DIALOGS.EINHEITEN_UMBUCHUNG.BESTAND_HINT', {
      menge: source.unit.menge,
      einheit: source.unit.einheit.arteh,
    })();
  });

  readonly targetUnitOptions = computed(() => {
    const source = this.source();
    if (!source) return [];
    return source.units.filter((o) => o.einheit.id !== source.unit.einheit.id);
  });
  readonly targetRows = signal<TargetRow[]>([]);
  readonly remainingTargetUnits = computed(() => {
    const used = new Set(this.targetRows().map((row) => row.unit.einheit.id));
    return this.targetUnitOptions().filter((o) => !used.has(o.einheit.id));
  });
  private proposalKey: string | null = null;

  readonly targetLocation = linkedSignal<Lagerplatz | null>(
    () => this.source()?.lagerplatz ?? null
  );

  readonly canContinue = computed(() => {
    switch (this.stepIndex()) {
      case 0:
        return !!this.source();
      case 1:
        return this.quantity() > 0;
      case 2: {
        const rows = this.targetRows();
        return (
          rows.length > 0 &&
          rows.every(
            (row) =>
              !row.loading && !!row.proposal && row.quantity <= proposedQuantity(row)
          ) &&
          rows.some((row) => row.quantity > 0)
        );
      }
      default:
        return !!this.targetLocation();
    }
  });

  isSelected(row: StockRow): boolean {
    const source = this.source();
    return !!source && stockRowKey(source) === stockRowKey(row);
  }

  selectSource(row: StockRow): void {
    if (row.unit.menge <= 0) return;
    this.source.set(row);
    this.stepIndex.set(1);
  }

  setTargetQuantity(row: TargetRow, quantity: number): void {
    this.patchTargetRow(row, { quantity });
  }

  async addTargetUnit(): Promise<void> {
    const result = await this.openSelectUnitDialog(this.remainingTargetUnits());
    if (result === 'DISMISSED') return;
    await this.addTargetRow(result);
  }

  async addTargetRow(unit: EinheitAuswahlItem): Promise<void> {
    if (this.targetRows().some((row) => row.unit.einheit.id === unit.einheit.id)) return;
    const row: TargetRow = { unit, proposal: null, quantity: 0, loading: true };
    this.targetRows.update((rows) => [...rows, row]);
    await this.loadProposal(row);
  }

  removeTargetRow(row: TargetRow): void {
    if (this.targetRows().length <= 1) return;
    this.targetRows.update((rows) => rows.filter((o) => o !== row));
  }

  back(): void {
    this.stepIndex.update((o) => Math.max(0, o - 1));
  }

  async next(): Promise<void> {
    if (!this.canContinue() || this.isLastStep()) return;
    if (this.stepIndex() === 1) await this.ensureTargetRows();
    this.stepIndex.update((o) => o + 1);
  }

  async book(): Promise<void> {
    const source = this.source();
    if (!this.canContinue() || !source) return;

    const body = buildStockTransfer({
      idartikel: this.artikelMenge().artikel.id,
      source,
      quantity: this.quantity(),
      targets: this.targetRows().map((row) => ({
        einheit: row.unit.einheit,
        quantity: row.quantity,
      })),
      targetLocation: this.targetLocation(),
    });

    try {
      await block(() => this.service.book(body), this.injector);
      this.messageBar.success(
        this.t('#KOMMISSIONIERUNG.DIALOGS.EINHEITEN_UMBUCHUNG.SUCCESS')
      );
      this.source.set(null);
      this.targetRows.set([]);
      this.proposalKey = null;
      this.stepIndex.set(0);
      this.stock.reload();
    } catch (e) {
      handleRequestError(e, this.injector, { show: true });
    }
  }

  // Proposals depend on source + quantity: reload every row when either changed since the last visit
  private async ensureTargetRows(): Promise<void> {
    const source = this.source();
    if (!source) return;
    const key = `${stockRowKey(source)}-${this.quantity()}`;
    if (key === this.proposalKey) return;
    this.proposalKey = key;

    if (this.targetRows().length === 0) {
      const options = this.targetUnitOptions();
      const pickingUnitId = this.artikelMenge().einheit?.id;
      const preferred = options.find((o) => o.einheit.id === pickingUnitId) ?? options[0];
      if (!preferred) return;
      await this.addTargetRow(preferred);
      return;
    }

    const rows = this.targetRows();
    await Promise.all(rows.map((row) => this.loadProposal(row)));
  }

  private async loadProposal(row: TargetRow): Promise<void> {
    const source = this.source();
    if (!source) return;
    this.patchTargetRow(row, { loading: true });
    try {
      const proposal = await this.service.loadProposal({
        quellMenge: {
          artikel: { id: this.artikelMenge().artikel.id },
          einheit: { id: source.unit.einheit.id },
          menge: this.quantity(),
        },
        zielEinheit: { id: row.unit.einheit.id },
      });
      this.patchTargetRow(row, {
        proposal,
        quantity: proposal?.zielMenge?.menge ?? 0,
        loading: false,
      });
    } catch (e) {
      this.patchTargetRow(row, { proposal: null, quantity: 0, loading: false });
      handleRequestError(e, this.injector, { show: true });
    }
  }

  private patchTargetRow(row: TargetRow, patch: Partial<TargetRow>): void {
    this.targetRows.update((rows) =>
      rows.map((o) => (o.unit.einheit.id === row.unit.einheit.id ? { ...o, ...patch } : o))
    );
  }
}
